/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import Tippy from "@tippyjs/react"
import { BaseButton, Button, ConditionalWrapper, Grid, Placeholder } from "@cd2/components"
import { H3, P } from "@cd2/text"
import { FloatingBox } from "../Components/Misc/FloatingBox"
import { brandColors, colors } from "../Components/EOSThemeProvider"
import { icons } from "../constants/icons"
import { zIndexes } from "../constants/zIndexes"
import { extractMessageFromError } from "../../server/utils/extractMessageFromError"

interface IHoverResourceLoaderProps<Data extends InitialData, InitialData = Data> {
  reloadKey?: any
  initialData?: InitialData
  inline?: boolean
  contentOnly?: boolean
  resourceName?: string
  renderInlineLink?: "block" | "inline"
  noLink?: boolean

  loadData(): Promise<Data>

  renderTarget(data: InitialData): React.ReactNode

  renderHoverContent(data: Data): React.ReactNode

  renderHoverContentTitle?(data: Data): React.ReactNode

  viewLink?(data: InitialData): string | undefined

  renderLoading?(): React.ReactNode

  color?(record: Data | null): string | undefined
}

export function HoverResourceLoader<Data extends InitialData, InitialData = Data>({
  reloadKey,
  loadData,
  initialData,
  renderHoverContent,
  renderTarget,
  inline,
  renderHoverContentTitle,
  viewLink,
  renderLoading,
  contentOnly,
  resourceName,
  renderInlineLink,
  color,
  noLink,
}: IHoverResourceLoaderProps<Data, InitialData>) {
  const [data, setData] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)
  const dataLoadingRef = useRef<null | number>(null)

  useEffect(() => {
    dataLoadingRef.current = null

    if (error) {
      setError(null)
    }
    if (data) {
      setData(null)
    }

    if (!initialData) {
      handleLoadData()
    }
  }, [reloadKey])

  async function handleLoadData() {
    try {
      if (dataLoadingRef.current) {
        return
      }
      const key = Math.random()
      dataLoadingRef.current = key

      const data = await loadData()

      // this check will trigger if the reloadKey changes while there is loadData request active
      // theoretically a call to loadData could be triggered after the reloadKey changes which is
      // why we store a random key and check it is the same as opposed to just checking a boolean
      if (dataLoadingRef.current !== key) {
        return
      }

      setData(data)
    } catch (err) {
      console.error(err)
      setError(extractMessageFromError(err))
    }
  }

  const viewLinkLocation = data || initialData ? viewLink?.((data ?? initialData)!) : undefined

  const content = useMemo(() => {
    if (data) {
      if (contentOnly) {
        return <FloatingBox color={color?.(data)}>{renderHoverContent(data)}</FloatingBox>
      } else {
        return (
          <FloatingBox
            color={color?.(data)}
            header={
              renderHoverContentTitle || viewLinkLocation ? (
                <Grid columns={[{ fr: 1 }, "max-content"]} alignItems="center">
                  {renderHoverContentTitle ? (
                    <ConditionalWrapper
                      condition={!!viewLinkLocation}
                      Wrapper={({ children }) =>
                        noLink ? (
                          <div>{children}</div>
                        ) : (
                          <Link to={data ? viewLinkLocation ?? "#" : "#"}>{children}</Link>
                        )
                      }
                    >
                      <div>
                        {resourceName && (
                          <span>
                            <P blockSpacing={0} size="small" weight={600} color="#666">
                              {resourceName}
                            </P>
                          </span>
                        )}
                        <H3
                          size="small"
                          blockSpacing={0}
                          color={viewLinkLocation ? brandColors.emerald : undefined}
                        >
                          {renderHoverContentTitle(data)}
                        </H3>
                      </div>
                    </ConditionalWrapper>
                  ) : (
                    <div />
                  )}
                  {viewLinkLocation && (
                    <a
                      href={data ? viewLinkLocation : "#"}
                      css={css`
                        text-decoration: none;
                        display: inline-block;
                        margin: -10px;
                      `}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button buttonStyle={"text"} rightIcon={icons.external}>
                        View
                      </Button>
                    </a>
                  )}
                </Grid>
              ) : undefined
            }
            css={css`
              min-width: 300px;
              --floating-box-header-padding: ${resourceName ? "8px 16px" : "16px"};
            `}
          >
            {renderHoverContent(data)}
          </FloatingBox>
        )
      }
    } else if (error) {
      return (
        <FloatingBox color={color?.(data)}>
          <P color="red">Failed to load resource.</P>
          <span
            css={css`
              color: #919191;
              font-size: 10px;
            `}
          >
            {error}
          </span>
        </FloatingBox>
      )
    } else if (renderLoading) {
      return <React.Fragment>{renderLoading()}</React.Fragment>
    } else {
      return null
    }
  }, [data, error])

  const targetData = data ?? initialData

  if (inline) {
    return content
  }

  return (
    <div
      css={css`
        display: inline-block;
      `}
    >
      <Tippy
        css={css`
          &.tippy-box {
            width: 100%;
            max-width: calc(100vw - 60px) !important;
          }

          .tippy-arrow {
            width: 16px;
            height: 16px;
            filter: drop-shadow(0 -1.5px 0.5px rgb(0 0 0 / 5%));
            color: #bbb;
          }

          &.tippy-box[data-placement^="top"] > .tippy-arrow {
            color: #bbb;
          }
        `}
        content={<div>{content}</div>}
        onShow={() => void handleLoadData()}
        duration={100}
        animation="fade"
        appendTo={document.body}
        zIndex={zIndexes.HOVER_LOADER}
        placement="bottom"
        trigger="click"
        interactive
      >
        <div
          css={css`
            cursor: pointer;
          `}
        >
          {targetData ? (
            <React.Fragment>
              <span
                css={css`
                  color: black;
                  text-decoration: 1px dashed underline ${colors.lightBorder};
                  text-underline-offset: 2px;
                  white-space: nowrap;
                  display: inline-block;
                `}
              >
                {renderTarget(targetData)}
              </span>
              {renderInlineLink && viewLink && !noLink && (
                <Link
                  to={viewLink(targetData) ?? "#"}
                  css={css`
                    display: ${renderInlineLink};
                    margin-left: -${renderInlineLink === "block" ? 3 : 0}px;
                  `}
                >
                  <BaseButton
                    buttonStyle="text"
                    theme={{
                      padding: 3,
                      fontSize: 11,
                      color: brandColors.emerald,
                      hoverColor: "rgba(0,0,0,0.05)",
                    }}
                  >
                    View
                  </BaseButton>
                </Link>
              )}
            </React.Fragment>
          ) : (
            <Placeholder
              width="100px"
              height={14}
              css={css`
                margin: -1px 0 -4px !important;
              `}
            />
          )}
        </div>
      </Tippy>
    </div>
  )
}
